import { useMainStore } from "../store/main.store";
import i18n from "../i18n";
import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from "axios";
import { isArray } from "lodash";
import { io, Socket } from "socket.io-client";
import * as Sentry from "@sentry/vue";

const IGNORED_HTTP_STATUSES = new Set([401, 403, 404]);

const reportHttpErrorToSentry = (error: AxiosError) => {
  const status = error.response?.status;
  if (!status || status < 400) return;
  if (IGNORED_HTTP_STATUSES.has(status)) return;

  Sentry.captureException(error, {
    tags: {
      http_status: String(status),
      api: "poi-backend",
    },
    extra: {
      url: error.config?.url,
      method: error.config?.method,
      responseData: error.response?.data,
    },
  });
};

const { t } = i18n.global;
const backend_url = import.meta.env.VITE_BACKEND_URL;
const backend_port = import.meta.env.VITE_BACKEND_PORT;
// console.log({ backend_url, backend_port });
let url: string = backend_url ? backend_url.toString() : "http://localhost:3000";
if (backend_port) {
  url += ":" + backend_port;
}

// For HTTP requests, add /api prefix in production
let httpUrl = url;
if (!url.includes("localhost") && !url.includes("/api")) {
  httpUrl = url + "/api";
}

// For WebSocket, always use base URL without /api
let wsUrl = url;
if (url.includes("/api")) {
  wsUrl = url.replace("/api", "");
}

console.log("HTTP URL:", httpUrl);
console.log("WebSocket URL:", wsUrl);

// Socket.io client
let socket: Socket | null = null;

const getSocket = (): Socket => {
  if (!socket) {
    const store = useMainStore();

    // For debugging - log the URL we're connecting to
    console.log("Connecting socket.io to:", wsUrl);

    // Create socket connection with explicit configuration
    socket = io(wsUrl, {
      path: "/socket.io/",
      transports: ["websocket", "polling"],
      autoConnect: false, // We'll connect manually after setup
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
    });

    // Add explicit error handler
    socket.on("connect_error", (error) => {
      console.error("Socket.io connection error:", error);
    });

    // Set up default reconnection behavior
    socket.io.on("reconnect_attempt", (attempt) => {
      console.log(`Socket.io attempting to reconnect... (attempt ${attempt})`);
    });

    socket.io.on("reconnect", () => {
      console.log("Socket.io reconnected");
      // Re-authenticate after reconnection
      if (store.userToken) {
        socket?.emit("authenticate", store.userToken);
      }
    });
  }
  return socket;
};

const disconnectSocket = () => {
  if (socket) {
    console.log("Disconnecting socket");
    socket.disconnect();
    socket = null;
  }
};

//REGULAR HTTP JSON
const http = axios.create({
  baseURL: httpUrl,
  headers: {
    "Content-type": "application/json",
  },
});

// Add a new method to the instance
http.interceptors.request.use((config) => {
  const store = useMainStore();
  const token = store.userToken;
  if (token && token !== "undefined" && config.headers) config.headers.Authorization = `Bearer ${token}`;

  const lang = i18n.global.locale;
  if (lang && config.headers) config.headers["Accept-Language"] = lang;
  if (sessionStorage.getItem("superAdminToken") && config.headers) config.headers["superAdminToken"] = sessionStorage.getItem("superAdminToken") ?? "";
  return config;
});

//HTTP FORM DATA
const httpForm = axios.create({
  baseURL: httpUrl,
  headers: {
    "Content-type": "multipart/form-data",
    "Content-Disposition": "multipart/form-data",
  },
});

httpForm.interceptors.request.use((config) => {
  const store = useMainStore();
  const token = store.userToken;

  if (token && config.headers) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

http.interceptors.response.use(
  (response) => {
    return response;
  },
  (error: AxiosError<any>) => {
    reportHttpErrorToSentry(error);

    if (error.message === "Network Error") {
      if (error.response?.status === 504) {
        throw {
          message: t("Something went wrong Please try again later"),
        };
      } else if (!navigator.onLine) {
        throw {
          message: t("you-are-not-connected-to-the-internet-verify-your-connection-and-then-try-again"),
        };
      } else {
        throw {
          message: t("couldnt-connect-to-the-server-please-try-again-later"),
        };
      }
    } else if (error.response?.data?.messageFields) {
      throw { message: error.response.data };
    } else if (error.response?.data?.message) {
      throw { message: error.response.data.message };
    } else if (error.response) {
      if (error.response.status === 500) {
        throw {
          message: t("something-went-wrong-please-try-again-later"),
        };
      }
      if (error.response.status === 401) {
        // logout logic
      }
      if (error.response.status === 403) {
        // redirect user to some home page since that action is not allowed
      }
      throw { ...error.response.data, statusCode: error.response.status };
    } else
      throw {
        message: t("something-went-wrong-please-try-again-later"),
      };
  },
);

httpForm.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    reportHttpErrorToSentry(error);
    return Promise.reject(error);
  },
);

async function getWithPagination(url: string, data: any = [], currentPage = 0): Promise<any> {
  currentPage = currentPage + 1;
  let parameters = "";
  const originalUrl = url;
  if (url.includes("?")) {
    parameters = url.split("?")[1];
    url = url.split("?")[0];
  }
  return http.get(`${url}?page=${currentPage}&${parameters}`).then((res: AxiosResponse) => {
    if (!res.data.totalPages || res.data.totalPages === 1) return res.data.data;
    if (isArray(res.data.data)) data = data.concat(res.data.data); //if data is not an array, concat will fail, so we check if it is an array with
    if (res.data.totalPages > currentPage) {
      return getWithPagination(originalUrl, data, currentPage);
    }
    return data;
  });
}
export { http, httpForm, getWithPagination, getSocket, disconnectSocket };
